export type Msg = { type: string; [key: string]: unknown };
type Listener = (msg: Msg) => void;

/**
 * A WebSocket to the server that signs in with a token, reconnects with backoff, and hands messages
 * to listeners by type ('*' gets everything). A phone that sleeps can leave the connection dead while it still looks
 * open (#859), so it checks with a ping whenever the page comes back to the screen or the network returns, and every
 * so often while it's showing; a dead connection is replaced straight away.
 */
export class Socket {
  private ws?: WebSocket;
  private listeners = new Map<string, Set<Listener>>();
  private retry = 0;
  private closed = false;
  private timer?: number;
  private pingTimer?: number;
  private checkEvery?: number;
  connected = false;

  constructor(
    private token: string,
    private role: 'parent' | 'device',
  ) {
    this.open();
    document.addEventListener('visibilitychange', this.check);
    window.addEventListener('online', this.check);
    window.addEventListener('pageshow', this.check);
    this.checkEvery = window.setInterval(() => document.visibilityState === 'visible' && this.check(), 25_000);
  }

  /** Make sure the connection is alive: reconnect now if it's down, otherwise expect any reply to a ping. */
  private check = () => {
    if (this.closed || document.visibilityState !== 'visible' || this.pingTimer) return;
    if (!this.connected || this.ws?.readyState !== WebSocket.OPEN) {
      if (this.ws?.readyState === WebSocket.CONNECTING) return;
      this.reconnect();
      return;
    }
    this.ws.send(JSON.stringify({ type: 'ping' }));
    this.pingTimer = window.setTimeout(() => {
      this.pingTimer = undefined;
      this.reconnect();
    }, 6000);
  };

  /** Drop the current connection (it may be dead without knowing it) and open a new one now. */
  private reconnect() {
    clearTimeout(this.timer);
    clearTimeout(this.pingTimer);
    this.pingTimer = undefined;
    const old = this.ws;
    this.ws = undefined; // its close, whenever it comes, is ignored
    old?.close();
    if (this.connected) {
      this.connected = false;
      this.emit({ type: 'connection', connected: false });
    }
    this.retry = 0;
    this.open();
  }

  private open() {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${proto}//${location.host}/ws`);
    this.ws = ws;
    ws.onopen = () => ws.send(JSON.stringify({ type: 'auth', token: this.token, role: this.role }));
    ws.onmessage = (e) => {
      let msg: Msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      // Anything from the server shows the connection is alive.
      clearTimeout(this.pingTimer);
      this.pingTimer = undefined;
      if (msg.type === 'pong') return;
      if (msg.type === 'hello') {
        this.retry = 0;
        this.connected = true;
        this.emit({ type: 'connection', connected: true });
      }
      this.emit(msg);
    };
    ws.onclose = (e) => {
      if (this.ws !== ws) return;
      const wasConnected = this.connected;
      this.connected = false;
      if (wasConnected) this.emit({ type: 'connection', connected: false });
      if (e.code === 4001 || e.code === 4003) this.emit({ type: 'signed-out', code: e.code });
      if (e.code === 4002) this.emit({ type: 'replaced' });
      if (this.closed || e.code === 4001 || e.code === 4003 || e.code === 4002) return;
      const delay = Math.min(30_000, 1000 * 2 ** this.retry++) * (0.75 + Math.random() / 2);
      this.timer = window.setTimeout(() => this.open(), delay);
    };
  }

  private emit(msg: Msg) {
    for (const key of [msg.type, '*']) this.listeners.get(key)?.forEach((fn) => fn(msg));
  }

  on(type: string, fn: Listener): () => void {
    if (!this.listeners.has(type)) this.listeners.set(type, new Set());
    this.listeners.get(type)!.add(fn);
    return () => this.listeners.get(type)?.delete(fn);
  }

  send(msg: Msg): boolean {
    if (this.ws?.readyState !== WebSocket.OPEN || !this.connected) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  close() {
    this.closed = true;
    clearTimeout(this.timer);
    clearTimeout(this.pingTimer);
    clearInterval(this.checkEvery);
    document.removeEventListener('visibilitychange', this.check);
    window.removeEventListener('online', this.check);
    window.removeEventListener('pageshow', this.check);
    this.ws?.close(1000);
  }
}
